/**
 * Hook for submitting signup information
 */
import { useMemo } from "react";
import { useSignup as useSignupContext } from "../../SignupContext";
import { handleApiError, InvalidStateError } from "../../utils/Errors";
import { useApiRequest } from "../../utils/useApiRequest";
/**
 * Hook for submitting user signup information
 * @returns RequestState object for signup submission
 */
export function useSubmitSignup() {
    const { apiClient, formValues } = useSignupContext();
    const { state: submitSignupState, execute: executeSubmitSignup, reset: resetSubmitSignupState, } = useApiRequest();
    return useMemo(() => ({
        execute: async () => {
            return executeSubmitSignup(async () => {
                // Validate required fields
                const missingFields = [];
                if (!formValues.curpDetails.firstName)
                    missingFields.push("firstName");
                if (!formValues.curpDetails.paternalLastName)
                    missingFields.push("paternalLastName");
                if (!formValues.email)
                    missingFields.push("email");
                if (!formValues.occupation)
                    missingFields.push("occupation");
                if (!formValues.curp)
                    missingFields.push("curp");
                // Address validation
                if (!formValues.address.street)
                    missingFields.push("address.street");
                if (!formValues.address.buildingNumber)
                    missingFields.push("address.buildingNumber");
                if (!formValues.address.colony)
                    missingFields.push("address.colony");
                if (!formValues.address.city)
                    missingFields.push("address.city");
                if (!formValues.address.postalCode)
                    missingFields.push("address.postalCode");
                // Check validity of each field
                const phoneNumberValid = formValues.phoneNumber && formValues.phoneNumber.length > 0;
                const emailValid = formValues.email && formValues.email.length > 0;
                const curpValid = formValues.curp && formValues.curp.length > 0;
                const occupationValid = formValues.occupation && formValues.occupation.length > 0;
                const incomeValid = true; // Income can be 0
                const addressValid = formValues.address.street &&
                    formValues.address.buildingNumber &&
                    formValues.address.colony &&
                    formValues.address.city &&
                    formValues.address.postalCode;
                const pinValid = !formValues.pin || (formValues.pin && formValues.pin.length > 0);
                const isFormValid = phoneNumberValid &&
                    emailValid &&
                    curpValid &&
                    occupationValid &&
                    incomeValid &&
                    addressValid &&
                    pinValid;
                if (!isFormValid || missingFields.length > 0) {
                    throw new InvalidStateError("Cannot submit signup with invalid or incomplete data", missingFields);
                }
                // Convert income to WireMoney format or null if zero
                const income = formValues.income === 0
                    ? null
                    : {
                        amountE5: formValues.income * 100000, // Convert to E5 format (amount * 10^5)
                        currencyCode: "MXN",
                    };
                // Prepare the signup request
                const request = {
                    firstName: formValues.curpDetails.firstName,
                    paternalLastName: formValues.curpDetails.paternalLastName,
                    maternalLastName: formValues.curpDetails.maternalLastName || null,
                    ...(formValues.pin ? { pin: formValues.pin } : {}),
                    email: formValues.email,
                    occupation: formValues.occupation,
                    income,
                    deviceState: formValues.deviceState,
                    address: {
                        countryIso2: "MX",
                        placeId: formValues.address.placeId,
                        addressMX: {
                            buildingNumber: formValues.address.buildingNumber,
                            interiorNumber: formValues.address.interiorNumber,
                            street: formValues.address.street,
                            colony: formValues.address.colony,
                            city: formValues.address.city,
                            state: formValues.address.state || null,
                            postalCode: formValues.address.postalCode,
                            delegation: null,
                            predetermined: false,
                            addressedTo: null,
                        },
                    },
                    curp: formValues.curp,
                    birthCountryIso: "MX", // Default to Mexico, can be overridden
                    ...(formValues.referralData && {
                        referralData: formValues.referralData,
                    }),
                };
                const response = await apiClient.signup(request);
                if (response.ok) {
                    const data = await response.json();
                    return data;
                }
                else {
                    const error = await handleApiError(response);
                    throw error;
                }
            });
        },
        isLoading: submitSignupState.isLoading,
        error: submitSignupState.error,
        data: submitSignupState.data,
        reset: resetSubmitSignupState,
    }), [
        apiClient,
        executeSubmitSignup,
        formValues,
        submitSignupState,
        resetSubmitSignupState,
    ]);
}
